import { useEffect, useState } from "react";
import { getEnvironmentRobot, updateEnvironmentRobot, type EnvironmentRobot } from "@/lib/api";
import { isAbortError } from "@/lib/abort-error";
import { robotLabel } from "@/lib/robot";
import { EnvironmentSettingsForm } from "./EnvironmentSettingsForm";

interface EnvironmentRobotPanelProps {
	environmentId: string;
}

const CAPTION_STYLE = {
	fontSize: "var(--text-caption)",
	lineHeight: "var(--leading-body)",
	fontFamily: "var(--font-sans)",
} as const;

const SAFETY_NOTES = [
	"ロボットが動くときは、停止ボタンをすぐに押せる人がそばにいるようにしてください。",
	"純正の VR や AgiBot のアプリでロボットを動かしている間は、このエージェントから動かさないでください。",
];

/** 「ロボットを使う」をオンにできない理由。オフにするのはいつでもできる。 */
function enableBlockedReason(data: EnvironmentRobot): string | null {
	if (!data.runnerOnline) {
		return "デバイスがオフラインのため、ロボットを使う設定にできません。デバイスがオンラインになってから開き直してください。";
	}
	if (data.runnerOutdated) {
		return "デバイスのランナーが古く、ロボットに対応していません。ランナーを更新してください。";
	}
	if (data.robot === null) {
		return "このデバイスからロボットが見つかっていません。ロボットにつながったデバイスでだけ使えます。";
	}
	return null;
}

/**
 * デバイスのロボットをこのエージェントから使うかの設定 (次の起動から反映)。1 台のロボットを同時に使えるのは 1 つのエージェントだけ
 * (重なりは起動のときに断られる)。
 */
export function EnvironmentRobotPanel({ environmentId }: EnvironmentRobotPanelProps) {
	const [data, setData] = useState<EnvironmentRobot | null>(null);
	const [useRobot, setUseRobot] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const controller = new AbortController();
		setLoadError(null);
		getEnvironmentRobot(environmentId, controller.signal)
			.then((result) => {
				setData(result);
				setUseRobot(result.useRobot);
			})
			.catch((err: unknown) => {
				if (isAbortError(err)) return;
				console.error("ロボットの設定を取得できませんでした", err);
				setLoadError(err instanceof Error ? err.message : "ロボットの設定の取得に失敗しました。");
			});
		return () => controller.abort();
	}, [environmentId]);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!data) return;
		setSubmitError(null);
		setSaved(false);
		setLoading(true);
		try {
			await updateEnvironmentRobot(environmentId, useRobot);
			setSaved(true);
		} catch (err) {
			console.error("ロボットの設定を保存できませんでした", err);
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	const blockedReason = data ? enableBlockedReason(data) : null;
	const disabled = loading || (!useRobot && blockedReason !== null);

	return (
		<EnvironmentSettingsForm
			testId="environment-robot-panel"
			onSubmit={handleSubmit}
			loading={loading}
			saved={saved}
			submitError={submitError}
			submitDisabled={data === null}
		>
			<span style={{ ...CAPTION_STYLE, color: "var(--fg-2)" }}>
				デバイスにつながったロボットを、このエージェントから動かせるようにします。ロボットを使う設定のエージェントは、ほかのエージェントがロボットを使っている間は起動できません。
			</span>
			<div
				data-testid="environment-robot-safety"
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 4,
					padding: "10px 14px",
					borderRadius: "var(--radius-3)",
					background: "var(--warning-subtle)",
					boxShadow: "0 0 0 1px var(--warning-line)",
					...CAPTION_STYLE,
					color: "var(--fg-1)",
				}}
			>
				{SAFETY_NOTES.map((note) => (
					<span key={note}>{note}</span>
				))}
			</div>
			{loadError && (
				<span
					data-testid="environment-robot-error"
					style={{ ...CAPTION_STYLE, color: "var(--danger)" }}
				>
					{loadError}
				</span>
			)}
			{data === null && !loadError && (
				<span
					data-testid="environment-robot-loading"
					style={{ ...CAPTION_STYLE, color: "var(--fg-3)" }}
				>
					読み込み中…
				</span>
			)}
			{data && blockedReason && (
				<span
					data-testid="environment-robot-blocked-reason"
					style={{ ...CAPTION_STYLE, color: "var(--fg-2)" }}
				>
					{blockedReason}
				</span>
			)}
			{data?.usedBy && (
				<span
					data-testid="environment-robot-used-by"
					style={{ ...CAPTION_STYLE, color: "var(--warning)" }}
				>
					{data.usedBy.environmentId === null
						? `${data.usedBy.name}が使用中です。このエージェントでロボットを使って起動するには、そのエージェントが停止するのを待ってください。`
						: `「${data.usedBy.name}」が使用中です。このエージェントでロボットを使って起動するには、先に「${data.usedBy.name}」を停止してください。`}
				</span>
			)}
			{data && (
				<label
					data-testid="environment-robot-option"
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						padding: "10px 12px",
						borderRadius: "var(--radius-3)",
						background: useRobot ? "var(--row-selected-bg)" : "transparent",
						boxShadow: useRobot ? "0 0 0 1px var(--accent)" : "var(--shadow-ring)",
						cursor: disabled ? "not-allowed" : "pointer",
						opacity: disabled ? 0.5 : 1,
						fontFamily: "var(--font-sans)",
					}}
				>
					<input
						type="checkbox"
						checked={useRobot}
						disabled={disabled}
						onChange={(e) => {
							setSaved(false);
							setUseRobot(e.target.checked);
						}}
						data-testid="environment-robot-use"
					/>
					<span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
						<span style={{ fontSize: "var(--text-ui)", fontWeight: 600, color: "var(--fg-1)" }}>
							ロボットを使う
						</span>
						<span
							data-testid="environment-robot-device"
							style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)" }}
						>
							{data.robot ? robotLabel(data.robot) : "ロボットなし"}
						</span>
					</span>
				</label>
			)}
		</EnvironmentSettingsForm>
	);
}
